import React from 'react'
import dynamic from 'next/dynamic'
import { getMfInstance } from '@/clients/moduleFederation'
import { type UnifiedHeaderProps } from '@/types'

// Method A: Import from koodo-shared-elements (UDS-based)
import {
  HeaderSkeleton,
  HeaderSkeletonWrapper,
  HeaderContentWrapper,
  SkeletonFallbackWrapper,
} from '@telus/koodo-shared-elements'

export type HeaderProps = UnifiedHeaderProps
type Lang = 'en' | 'fr'

const REMOTE_MODULE = 'koodoGlobalElementsMfe/Header'
const NullComponent = () => null

// Creates a dynamic Header component with locale-aware skeleton
const createHeaderComponent = (lang: Lang) =>
  dynamic<HeaderProps>(
    () => {
      const mfInstance = getMfInstance()
      if (!mfInstance) {
        console.warn('[Module Federation] Instance not initialized')
        return Promise.resolve({ default: NullComponent })
      }

      return mfInstance
        .loadRemote<{ default: React.ComponentType<HeaderProps> }>(REMOTE_MODULE)
        .then((module) => module ?? { default: NullComponent })
        .catch((error) => {
          console.error('[Module Federation] Failed to load Header:', error)
          return { default: NullComponent }
        })
    },
    {
      ssr: false,
      loading: () => <HeaderSkeleton lang={lang} />,
    }
  )

// Pre-create dynamic components by language to avoid re-creation on each render
const headerComponents: Record<Lang, React.ComponentType<HeaderProps>> = {
  en: createHeaderComponent('en'),
  fr: createHeaderComponent('fr'),
}

const Header: React.FC<HeaderProps> = (props) => {
  const lang = props.locale?.lang ?? 'en'
  const HeaderComponent = headerComponents[lang]
  return (
    <HeaderSkeletonWrapper>
      <HeaderContentWrapper>
        <HeaderComponent {...props} />
      </HeaderContentWrapper>
      <SkeletonFallbackWrapper>
        <HeaderSkeleton lang={lang} />
      </SkeletonFallbackWrapper>
    </HeaderSkeletonWrapper>
  )
}

export default Header
